import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Typography,
    Button,
    Card,
    Divider,
    Chip,
    Stack,
    TextField,
    Collapse,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import { useParams, useNavigate } from "react-router-dom";
import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/styles.min.css";
import CartToast from "../components/CartToast";
import { getProductById, getCategoryBySlug } from "../../utils/storage";

const WHATSAPP_NUMBER = "918249643544";

export default function CategoryDetails() {
    const { slug, productId } = useParams();
    const navigate = useNavigate();

    const [product, setProduct] = useState(null);
    const [category, setCategory] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    const [enquiryOpen, setEnquiryOpen] = useState(false);
    const [enquiryData, setEnquiryData] = useState({
        name: "", email: "", phone: "", quantity: "", message: "",
    });
    const [enquirySent, setEnquirySent] = useState(false);
    const [toast, setToast] = useState({ visible: false, message: "", type: "success" });

    const showToast = (message, type = "success") => {
        setToast({ visible: true, message, type });
        setTimeout(() => setToast((t) => ({ ...t, visible: false })), 2800);
    };

    useEffect(() => {
        const styleId = "quill-mobile-fix";
        if (!document.getElementById(styleId)) {
            const style = document.createElement("style");
            style.id = styleId;
            style.textContent = `
                @media (max-width: 600px) {
                    .quill-description,
                    .quill-description * {
                        white-space: normal !important;
                        word-break: normal !important;
                        overflow-wrap: break-word !important;
                    }
                }
            `;
            document.head.appendChild(style);
        }
    }, []);

    useEffect(() => {
        const prod = getProductById(productId);
        if (!prod) { setNotFound(true); return; }
        const cat = getCategoryBySlug(slug);
        setProduct(prod);
        setCategory(cat);
        setSelectedImage(prod.images?.[0] || null);
        const titleCase = (str) =>
            str.replace(
                /\b\w/g,
                char => char.toUpperCase()
            );

        document.title = `${titleCase(prod.name)} | Tecniqa`;
    }, [slug, productId]);

    if (notFound) {
        return (
            <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2 }}>
                <Typography variant="h5" fontWeight={700}>Product not found</Typography>
                <Button variant="outlined" onClick={() => navigate(-1)}>Go Back</Button>
            </Box>
        );
    }

    if (!product) return null;

    const handleWhatsApp = () => {
        const text = encodeURIComponent(
            `Hello! I'm interested in the product: *${product.name}* (Brand: ${product.brand}). Could you please share the pricing details?`
        );
        window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, "_blank");
    };

    const handleEnquirySubmit = () => {
        if (!enquiryData.name || !enquiryData.email || !enquiryData.phone) {
            showToast("Please fill in all required fields.", "warning");
            return;
        }
        // console.log("Enquiry:", { product: product.name, ...enquiryData });
        setEnquirySent(true);
        showToast("Enquiry submitted successfully!", "success");
        setTimeout(() => {
            setEnquiryOpen(false);
            setEnquirySent(false);
            setEnquiryData({ name: "", email: "", phone: "", quantity: "", message: "" });
        }, 2000);
    };

    // Parse plain text from HTML highlights (from Quill)
    const highlightItems = (() => {
        const temp = document.createElement("div");
        temp.innerHTML = product.highlights || "";
        const listItems = temp.querySelectorAll("li");
        if (listItems.length > 0) {
            return Array.from(listItems).map((li) => li.textContent.trim());
        }
        const text = temp.innerText || temp.textContent || "";
        return text.split("\n").map((s) => s.trim()).filter(Boolean);
    })();

    return (
        <Box sx={{
            background: "linear-gradient(to bottom, #f6f7f8, #eef1f3)",
            minHeight: "100vh",
            py: 3,
            px: 2,
            overflowX: "hidden",
            "@media (max-width: 600px)": {
                py: 2,
                px: 1,
            },
        }}>
            <Box sx={{ maxWidth: 1180, mx: "auto", minWidth: 0, overflow: "hidden" }}>

                {/* BACK */}
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{
                        textTransform: "none", borderRadius: "12px", fontWeight: 600,
                        mb: 2.5, color: "#1d2939", borderColor: "#d0d5dd", px: 2.2,
                        py: 0.8, fontSize: "13px", backgroundColor: "#fff",
                        "&:hover": { borderColor: "#98a2b3", backgroundColor: "#f8fafc" },
                        // Mobile: slightly smaller button
                        "@media (max-width: 600px)": {
                            mb: 1.5,
                            px: 1.5,
                            fontSize: "12px",
                        },
                    }}
                >
                    Back
                </Button>

                {/* MAIN CARD */}
                <Card sx={{
                    p: { xs: 2, md: 3 }, borderRadius: "28px",
                    background: "rgba(255,255,255,0.92)",
                    border: "1px solid rgba(226,232,240,0.9)",
                    boxShadow: "0 10px 35px rgba(15,23,42,0.06)",
                    backdropFilter: "blur(10px)",
                    // Mobile: reduce border radius and padding
                    "@media (max-width: 600px)": {
                        borderRadius: "18px",
                        p: 1.5,
                    },
                }}>
                    <Box sx={{
                        display: "flex", gap: 4, alignItems: "flex-start",
                        flexDirection: { xs: "column", md: "row" },
                        // Mobile: reduce gap between image section and info section
                        "@media (max-width: 600px)": {
                            gap: 2,
                        },
                    }}>

                        {/* LEFT — Images */}
                        <Box sx={{
                            width: { xs: "100%", md: "42%" },
                            display: "flex",
                            gap: 1.5,
                            // Mobile: constrain height so image block doesn't dominate the screen
                            "@media (max-width: 600px)": {
                                gap: 1,
                            },
                        }}>
                            {/* Thumbnails */}
                            <Box sx={{
                                display: "flex", flexDirection: "column", gap: 1,
                                // Mobile: smaller thumbnails
                                "@media (max-width: 600px)": {
                                    gap: 0.75,
                                },
                            }}>
                                {product.images?.map((img, index) => (
                                    <Box
                                        key={index}
                                        onMouseEnter={() => setSelectedImage(img)}
                                        onClick={() => setSelectedImage(img)}
                                        sx={{
                                            width: 62, height: 62,
                                            border: selectedImage === img ? "2px solid #111827" : "1px solid #d0d5dd",
                                            borderRadius: "14px", overflow: "hidden", cursor: "pointer",
                                            backgroundColor: "#fff", p: 0.5,
                                            transition: "0.3s ease",
                                            "&:hover": { borderColor: "#667085", transform: "translateY(-2px)" },
                                            // Mobile: smaller thumbnails
                                            "@media (max-width: 600px)": {
                                                width: 48,
                                                height: 48,
                                                borderRadius: "10px",
                                            },
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={img}
                                            alt=""
                                            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                                        />
                                    </Box>
                                ))}
                            </Box>

                            {/* Main image */}
                            <Box sx={{
                                flex: 1,
                                border: "1px solid #e4e7ec",
                                borderRadius: "24px",
                                background: "linear-gradient(145deg,#ffffff,#f8fafc)",
                                p: 2, minHeight: 390,
                                display: "flex", justifyContent: "center", alignItems: "center",
                                overflow: "hidden",
                                // Mobile: reduce min height so it doesn't push content far down
                                "@media (max-width: 600px)": {
                                    minHeight: 240,
                                    borderRadius: "16px",
                                    p: 1.5,
                                },
                            }}>
                                {selectedImage ? (
                                    <InnerImageZoom
                                        src={selectedImage}
                                        zoomSrc={selectedImage}
                                        zoomType="hover"
                                        zoomScale={1.5}
                                        hideHint={true}
                                        width={360}
                                        height={360}
                                        imgAttributes={{ style: { objectFit: "contain", width: "100%", height: "100%" } }}
                                    />
                                ) : (
                                    <Box sx={{ color: "#ccc", fontSize: "64px", textAlign: "center" }}>
                                        📦
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* RIGHT — Info */}
                        <Box sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            "@media (max-width: 600px)": {
                                width: "100%",
                            },
                        }}>
                            <Typography
                                variant="h4"
                                fontWeight={800}
                                sx={{
                                    color: "#111827", lineHeight: 1.25,
                                    fontSize: { xs: "28px", md: "36px" },
                                    // Mobile: tighter font size
                                    "@media (max-width: 600px)": {
                                        fontSize: "22px",
                                        lineHeight: 1.3,
                                    },
                                }}
                            >
                                {product.name}
                            </Typography>

                            <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{
                                    mt: 1.5, flexWrap: "wrap",
                                    // Mobile: tighter top margin
                                    "@media (max-width: 600px)": {
                                        mt: 1,
                                        gap: 0.75,
                                    },
                                }}
                            >
                                <Typography sx={{ color: "#667085", fontSize: "14px" }}>
                                    Brand :
                                    <Box component="span" sx={{ color: "#111827", fontWeight: 700, ml: 0.7 }}>
                                        {product.brand}
                                    </Box>
                                </Typography>
                                <Chip
                                    label={product.availability}
                                    size="small"
                                    sx={{ backgroundColor: "#ecfdf3", color: "#027a48", fontWeight: 700, borderRadius: "8px" }}
                                />
                                <Chip
                                    label={product.badge}
                                    size="small"
                                    sx={{ backgroundColor: "#eff6ff", color: "#1d4ed8", fontWeight: 700, borderRadius: "8px" }}
                                />
                            </Stack>

                            {/* Price */}
                            <Box sx={{
                                mt: 2.5,
                                display: "inline-flex", alignItems: "center", gap: 1,
                                background: "linear-gradient(135deg,#f8fafc,#f1f5f9)",
                                border: "1px dashed #cbd5e1", borderRadius: "14px", px: 2.5, py: 1.2,
                                // Mobile: slightly smaller padding
                                "@media (max-width: 600px)": {
                                    mt: 1.5,
                                    px: 1.8,
                                    py: 1,
                                },
                            }}>
                                <Typography fontWeight={700} sx={{ color: "#475467", fontSize: "15px", letterSpacing: "0.3px" }}>
                                    Price:
                                </Typography>
                                <Typography fontWeight={800} sx={{ color: "#111827", fontSize: "17px", fontStyle: "italic" }}>
                                    As per price
                                </Typography>
                            </Box>

                            {/* Short Description */}
                            {product.shortDescription && (
                                <Typography sx={{
                                    mt: 2.5, color: "#667085", lineHeight: 1.9, fontSize: "14px",
                                    maxWidth: "95%", overflowWrap: "break-word", whiteSpace: "normal",
                                    "@media (max-width: 600px)": {
                                        mt: 1.5,
                                        fontSize: "13px",
                                        lineHeight: 1.75,
                                        maxWidth: "100%",
                                        wordBreak: "keep-all",
                                        overflowWrap: "normal",
                                        hyphens: "none",
                                    },
                                }}>
                                    {product.shortDescription}
                                </Typography>
                            )}

                            {/* Highlights */}
                            {highlightItems.length > 0 && (
                                <Box sx={{
                                    mt: 3,
                                    background: "linear-gradient(135deg,#f8fafc,#f1f5f9)",
                                    border: "1px solid #e2e8f0", borderRadius: "18px", p: 2,
                                    // Mobile: reduce top margin and padding
                                    "@media (max-width: 600px)": {
                                        mt: 2,
                                        p: 1.5,
                                        borderRadius: "14px",
                                    },
                                }}>
                                    <Typography fontWeight={700} sx={{ color: "#111827", mb: 1.5, fontSize: "16px" }}>
                                        Highlights
                                    </Typography>
                                    <Grid container spacing={1}>
                                        {highlightItems.map((item, index) => (
                                            // Mobile: full-width highlight items instead of 2 columns
                                            <Grid
                                                size={{ xs: 12, sm: 6 }}
                                                key={index}
                                            >
                                                <Typography sx={{ fontSize: "13px", color: "#475467" }}>
                                                    • {item}
                                                </Typography>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                            {/* Action Buttons */}
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                sx={{
                                    mt: 4,
                                    // Mobile: reduce top margin, full-width stacked buttons
                                    "@media (max-width: 600px)": {
                                        mt: 2.5,
                                        spacing: 1.5,
                                    },
                                }}
                            >
                                <Button
                                    variant="contained"
                                    startIcon={<WhatsAppIcon />}
                                    onClick={handleWhatsApp}
                                    sx={{
                                        background: "linear-gradient(135deg,#25d366,#128c7e)",
                                        py: 1.2, px: 3, borderRadius: "14px",
                                        textTransform: "none", fontWeight: 700, fontSize: "14px", color: "#fff",
                                        "&:hover": { background: "linear-gradient(135deg,#20c45e,#0f7a6e)" },
                                        // Mobile: full width
                                        "@media (max-width: 600px)": {
                                            width: "100%",
                                            py: 1.4,
                                            fontSize: "14px",
                                        },
                                    }}
                                >
                                    Order on WhatsApp
                                </Button>

                                <Button
                                    variant="contained"
                                    startIcon={<HelpOutlineOutlinedIcon />}
                                    onClick={() => setEnquiryOpen((v) => !v)}
                                    sx={{
                                        background: enquiryOpen
                                            ? "linear-gradient(135deg,#c89c22,#e9c454)"
                                            : "linear-gradient(135deg,#d4af37,#f5d76e)",
                                        py: 1.2, px: 3, borderRadius: "14px",
                                        textTransform: "none", fontWeight: 700, fontSize: "14px", color: "#111827",
                                        boxShadow: "0 10px 25px rgba(212,175,55,0.28)",
                                        "&:hover": { background: "linear-gradient(135deg,#c89c22,#e9c454)" },
                                        // Mobile: full width
                                        "@media (max-width: 600px)": {
                                            width: "100%",
                                            py: 1.4,
                                            fontSize: "14px",
                                        },
                                    }}
                                >
                                    {enquiryOpen ? "Close Enquiry" : "Enquiry About Product"}
                                </Button>
                            </Stack>

                            {/* Enquiry Form */}
                            <Collapse in={enquiryOpen} timeout={350}>
                                <Box sx={{
                                    mt: 2.5,
                                    background: "linear-gradient(135deg,#fffbeb,#fefce8)",
                                    border: "1px solid #fde68a", borderRadius: "20px", p: 2.5,
                                    // Mobile: slightly less padding
                                    "@media (max-width: 600px)": {
                                        mt: 2,
                                        p: 1.75,
                                        borderRadius: "16px",
                                    },
                                }}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                        <Typography fontWeight={700} sx={{ color: "#111827", fontSize: "15px" }}>
                                            Product Enquiry
                                        </Typography>
                                        <Box
                                            onClick={() => setEnquiryOpen(false)}
                                            sx={{ cursor: "pointer", color: "#667085", display: "flex", alignItems: "center", "&:hover": { color: "#111827" } }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </Box>
                                    </Stack>

                                    {enquirySent ? (
                                        <Typography sx={{ textAlign: "center", color: "#027a48", fontWeight: 700, py: 2, fontSize: "15px" }}>
                                            ✅ Enquiry submitted! We'll get back to you soon.
                                        </Typography>
                                    ) : (
                                        <Grid container spacing={2}>
                                            {[
                                                { label: "Full Name *", key: "name", type: "text", sm: 6 },
                                                { label: "Email Address *", key: "email", type: "email", sm: 6 },
                                                { label: "Phone Number *", key: "phone", type: "text", sm: 6 },
                                                { label: "Quantity Required", key: "quantity", type: "number", sm: 6 },
                                            ].map(({ label, key, type, sm }) => (
                                                <Grid size={{ xs: 12, sm: sm }} key={key}>
                                                    <TextField
                                                        label={label} size="small" fullWidth type={type}
                                                        value={enquiryData[key]}
                                                        onChange={(e) => setEnquiryData((d) => ({ ...d, [key]: e.target.value }))}
                                                        sx={inputSx}
                                                    />
                                                </Grid>
                                            ))}
                                            <Grid size={12}>
                                                <TextField
                                                    label="Message / Requirements" size="small" fullWidth multiline rows={3}
                                                    value={enquiryData.message}
                                                    onChange={(e) => setEnquiryData((d) => ({ ...d, message: e.target.value }))}
                                                    sx={inputSx}
                                                />
                                            </Grid>
                                            <Grid size={12}>
                                                <Button
                                                    variant="contained"
                                                    endIcon={<SendIcon />}
                                                    fullWidth
                                                    onClick={handleEnquirySubmit}
                                                    sx={{
                                                        background: "linear-gradient(135deg,#111827,#1f2937)",
                                                        py: 1.1, borderRadius: "12px", textTransform: "none",
                                                        fontWeight: 700, fontSize: "14px",
                                                        boxShadow: "0 8px 20px rgba(17,24,39,0.2)",
                                                        "&:hover": { background: "linear-gradient(135deg,#0f172a,#111827)" },
                                                        // Mobile: slightly taller tap target
                                                        "@media (max-width: 600px)": {
                                                            py: 1.4,
                                                        },
                                                    }}
                                                >
                                                    Submit Enquiry
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Box>
                            </Collapse>
                        </Box>
                    </Box>

                    {/* LOWER SECTION */}
                    <Divider sx={{ my: 4, borderColor: "#eaecf0",
                        // Mobile: reduce vertical spacing around divider
                        "@media (max-width: 600px)": {
                            my: 2.5,
                        },
                    }} />

                    {/* Product Description — full width */}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{
                                color: "#111827", mb: 1.5,
                                // Mobile: slightly smaller heading
                                "@media (max-width: 600px)": {
                                    fontSize: "16px",
                                    mb: 1,
                                },
                            }}
                        >
                            Product Description
                        </Typography>

                        {product.description ? (
                            <Box
                                sx={{
                                    color: "#667085",
                                    lineHeight: 1.9,
                                    fontSize: "14px",
                                    "& p": {
                                        margin: "0 0 8px 0",
                                        whiteSpace: "normal",
                                    },
                                    "& span": {
                                        whiteSpace: "normal !important",
                                    },
                                    "& ul, & ol": { paddingLeft: "20px", margin: "0 0 8px 0" },
                                    "& li": {
                                        marginBottom: "4px",
                                        whiteSpace: "normal",
                                    },
                                }}
                                className="quill-description"
                                style={{
                                    whiteSpace: "normal",
                                    wordBreak: "normal",
                                    overflowWrap: "break-word",
                                    width: "100%",
                                }}
                                dangerouslySetInnerHTML={{ __html: product.description.replace(/&nbsp;/g, " ") }}
                            />
                        ) : (
                            <Typography sx={{ color: "#aaa", fontSize: "14px" }}>
                                No description provided.
                            </Typography>
                        )}
                    </Box>

                    {/* Specifications — below description */}
                    <Box sx={{
                        mt: 4,
                        // Mobile: reduce top margin
                        "@media (max-width: 600px)": {
                            mt: 2.5,
                        },
                    }}>
                        <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{
                                color: "#111827", mb: 1.5,
                                // Mobile: slightly smaller heading
                                "@media (max-width: 600px)": {
                                    fontSize: "16px",
                                    mb: 1,
                                },
                            }}
                        >
                            Specifications
                        </Typography>

                        {/* Mobile: wrap into 2-column grid instead of a single overflowing row */}
                        <Box sx={{
                            display: "flex", gap: 1.5, flexWrap: "nowrap",
                            "@media (max-width: 600px)": {
                                flexWrap: "wrap",
                                gap: 1,
                            },
                        }}>
                            {[
                                { label: "Brand", value: product.brand },
                                { label: "Category", value: category?.name || product.categorySlug },
                                { label: "Availability", value: product.availability },
                                { label: "Warranty", value: product.warranty },
                            ].map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        flex: "1 1 0",
                                        background: "#f8fafc",
                                        border: "1px solid #e4e7ec",
                                        borderRadius: "14px",
                                        p: 1.5,
                                        // Mobile: each card takes ~half width (2 per row)
                                        "@media (max-width: 600px)": {
                                            flex: "1 1 calc(50% - 8px)",
                                            minWidth: "calc(50% - 8px)",
                                            borderRadius: "12px",
                                            p: 1.25,
                                        },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            color: "#111827",
                                            fontSize: "13px",
                                            mb: 0.5,
                                        }}
                                    >
                                        {item.label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: "#667085",
                                            fontSize: "13px",
                                            // Mobile: allow long values to wrap
                                            "@media (max-width: 600px)": {
                                                fontSize: "12px",
                                                wordBreak: "break-word",
                                            },
                                        }}
                                    >
                                        {item.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Card>
            </Box>

            <CartToast visible={toast.visible} message={toast.message} type={toast.type} />
        </Box>
    );
}

const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "12px", backgroundColor: "#fff", fontSize: "13px",
        "& fieldset": { borderColor: "#e4e7ec" },
        "&:hover fieldset": { borderColor: "#667085" },
        "&.Mui-focused fieldset": { borderColor: "#111827" },
    },
    "& .MuiInputLabel-root": { fontSize: "13px", color: "#667085" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#111827" },
};